import { Autocomplete, TextField, Typography, Box, Divider } from '@mui/material';
import { Product } from '@core/models/product.model';
import React, { useMemo, useState } from 'react';
import {
    EpcisProductLinePreview,
    EpcisPreviewSummary,
    findIlmdScanMismatches,
    findIlmdStagingMismatches,
    InboundProductLineStaging,
    productLineLabel,
} from './epcis-preview.helpers';
import InboundSerialNumbersEditor from './InboundSerialNumbersEditor';
import EpcisProductIdentityPreviewPanel from './EpcisProductIdentityPreviewPanel';
import PackageDataMatrixScanField from 'components/batch/PackageDataMatrixScanField';
import { ISgtinScanValidation } from '@core/models/sgtin-scan.model';

type LineRowProps = {
    line: InboundProductLineStaging;
    index: number;
    disabled: boolean;
    products: Product[];
    previewLine?: EpcisProductLinePreview;
    previewJson?: string | null;
    onUpdate: (index: number, patch: Partial<InboundProductLineStaging>) => void;
};

const InboundProductLineRow: React.FC<LineRowProps> = ({
    line,
    index,
    disabled,
    products,
    previewLine,
    previewJson,
    onUpdate,
}) => {
    const [scanMismatch, setScanMismatch] = useState<string | null>(null);
    const stagingIlmdWarnings = useMemo(
        () =>
            findIlmdStagingMismatches(
                previewLine?.lotNumber,
                previewLine?.expiryDate,
                line.lotNumber,
                line.expiryDate
            ),
        [previewLine?.lotNumber, previewLine?.expiryDate, line.lotNumber, line.expiryDate]
    );
    const selectedProduct = products.find((p) => p.id === line.productId) ?? null;
    const ndcMismatch =
        previewLine?.ndcHint &&
        selectedProduct?.ndcCode &&
        previewLine.ndcHint.replace(/\D/g, '') !== selectedProduct.ndcCode.replace(/\D/g, '');
    const label = previewLine ? productLineLabel(previewLine) : line.epcisProductLineKey;

    const onScanValidated = (serial: string, validation: ISgtinScanValidation) => {
        const nextSerials = line.serialNumbers.includes(serial)
            ? line.serialNumbers
            : [...line.serialNumbers, serial];
        onUpdate(index, { serialNumbers: nextSerials });
        const warnings = findIlmdScanMismatches(
            previewLine?.lotNumber,
            previewLine?.expiryDate,
            validation.lot,
            validation.expiry
        );
        setScanMismatch(warnings.length ? warnings.join('; ') : null);
    };

    return (
        <Box key={line.stagingRowKey || index} className="rounded-md border border-gray-200 p-4">
            <Typography variant="subtitle2" className="mb-3 font-medium">
                {label}
            </Typography>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Autocomplete
                    disabled={disabled}
                    className="md:col-span-2"
                    options={products}
                    getOptionLabel={(option: Product) => {
                        const code = option.ndcCode || option.gtin || option.barcode || '';
                        return option.itemName
                            ? code
                                ? `${option.itemName} (${code})`
                                : option.itemName
                            : `Product #${option.id}`;
                    }}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={selectedProduct}
                    onChange={(_, newValue) => onUpdate(index, { productId: newValue?.id ?? null })}
                    renderInput={(params) => <TextField {...params} label="Product *" size="small" required />}
                    sx={{ width: '100%' }}
                />
                {ndcMismatch ? (
                    <Typography variant="caption" color="error" className="md:col-span-2 -mt-1">
                        Catalog product NDC does not match EPCIS line.
                    </Typography>
                ) : null}
                <TextField
                    disabled={disabled}
                    fullWidth
                    size="small"
                    label="Lot number *"
                    value={line.lotNumber}
                    onChange={(e) => onUpdate(index, { lotNumber: e.target.value })}
                />
                <TextField
                    disabled={disabled}
                    fullWidth
                    size="small"
                    label="Expiry date *"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={line.expiryDate}
                    onChange={(e) => onUpdate(index, { expiryDate: e.target.value })}
                />
                {stagingIlmdWarnings.length > 0 ? (
                    <Typography variant="caption" color="warning.main" className="md:col-span-2 -mt-1">
                        Supplier ILMD mismatch: {stagingIlmdWarnings.join('; ')}
                    </Typography>
                ) : null}
                <TextField
                    disabled={disabled}
                    fullWidth
                    size="small"
                    label="Quantity"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={line.quantity}
                    onChange={(e) => onUpdate(index, { quantity: e.target.value })}
                />
                <TextField
                    disabled={disabled}
                    fullWidth
                    size="small"
                    label="Actual price"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={line.actualPrice}
                    onChange={(e) => onUpdate(index, { actualPrice: e.target.value })}
                />
                <TextField
                    disabled={disabled}
                    fullWidth
                    size="small"
                    label="Pallet SSCC *"
                    value={line.palletSscc}
                    onChange={(e) => onUpdate(index, { palletSscc: e.target.value })}
                />
                <TextField
                    disabled={disabled}
                    fullWidth
                    size="small"
                    label="Pallet weight (kg)"
                    type="number"
                    inputProps={{ min: 0, step: 0.1 }}
                    value={line.palletWeightKg}
                    onChange={(e) => onUpdate(index, { palletWeightKg: e.target.value })}
                />
            </div>
            <EpcisProductIdentityPreviewPanel
                productId={line.productId}
                sgtinItemKey={previewLine?.sgtinItemKey}
            />
            {line.productId ? (
                <Box className="mt-3">
                    <PackageDataMatrixScanField
                        productId={line.productId}
                        disabled={disabled}
                        existingSerials={line.serialNumbers}
                        onValidSerial={onScanValidated}
                    />
                    {scanMismatch ? (
                        <Typography variant="caption" color="warning.main" className="mt-1 block">
                            ILMD mismatch: {scanMismatch}
                        </Typography>
                    ) : null}
                </Box>
            ) : null}
            <Divider className="my-3" />
            <InboundSerialNumbersEditor
                serials={line.serialNumbers}
                onChange={(serialNumbers) => onUpdate(index, { serialNumbers })}
                disabled={disabled}
                label={`Serial numbers (${line.serialNumbers.length})`}
                previewJson={previewJson}
                selectedProductLineKey={line.epcisProductLineKey}
            />
        </Box>
    );
};

type Props = {
    lines: InboundProductLineStaging[];
    onChange: (lines: InboundProductLineStaging[]) => void;
    disabled?: boolean;
    products: Product[];
    previewSummary?: EpcisPreviewSummary | null;
    previewJson?: string | null;
};

const InboundProductLinesEditor: React.FC<Props> = ({
    lines,
    onChange,
    disabled = false,
    products,
    previewSummary,
    previewJson,
}) => {
    const updateLine = (index: number, patch: Partial<InboundProductLineStaging>) => {
        const next = [...lines];
        next[index] = { ...next[index], ...patch };
        onChange(next);
    };

    if (lines.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" className="md:col-span-2">
                No EPCIS product lines detected. Re-run validation or enter staging manually.
            </Typography>
        );
    }

    return (
        <div className="md:col-span-2 flex flex-col gap-4">
            {lines.map((line, index) => (
                <InboundProductLineRow
                    key={line.stagingRowKey || index}
                    line={line}
                    index={index}
                    disabled={disabled}
                    products={products}
                    previewLine={previewSummary?.productLines.find(
                        (l) => l.lineKey === line.epcisProductLineKey
                    )}
                    previewJson={previewJson}
                    onUpdate={updateLine}
                />
            ))}
        </div>
    );
};

export default InboundProductLinesEditor;
